import Model, { attr, belongsTo } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import type { ModelRegistry } from 'ember-data/types/registries/model';

export enum DisputeStage {
  inquiry = 'inquiry',
  chargeback = 'chargeback',
}

export enum DisputeStatus {
  needsResponse = 'needs_response',
  underReview = 'under_review',
  won = 'won',
  lost = 'lost',
  closed = 'closed',
  prevented = 'prevented',
}

export enum DisputeTargetType {
  payment = 'payment',
  securityDeposit = 'security_deposit',
}

export interface IDisputePaymentMethod {
  type?: string;
  brand?: string;
  network?: string;
  case_type?: string;
  network_reason_code?: string;
}

export interface IDisputeEvidence {
  submitted: boolean;
  staged: boolean;
  past_due: boolean;
  submission_count: number;
  fields: Record<string, string>;
  files: Record<string, string>;
}

export interface IDisputeBalanceTransaction {
  external_id?: string;
  amount?: number;
  fee?: number;
  net?: number;
  currency?: string;
  reporting_category?: string;
  created_at?: string;
}

export interface IDisputeDetail {
  status?: string;
  reason?: string;
  charge_id?: string;
  payment_intent_id?: string;
  charge_refundable?: boolean;
  enhanced_eligibility_types?: string[];
  payment_method?: IDisputePaymentMethod;
  evidence?: IDisputeEvidence;
  balance_transactions?: IDisputeBalanceTransaction[];
}

export const DISPUTE_EVIDENCE_TEXT_FIELDS = [
  'access_activity_log',
  'billing_address',
  'cancellation_policy_disclosure',
  'cancellation_rebuttal',
  'customer_email_address',
  'customer_name',
  'customer_purchase_ip',
  'duplicate_charge_explanation',
  'duplicate_charge_id',
  'product_description',
  'refund_policy_disclosure',
  'refund_refusal_explanation',
  'service_date',
  'shipping_address',
  'shipping_carrier',
  'shipping_date',
  'shipping_tracking_number',
  'uncategorized_text',
] as const;

export const DISPUTE_EVIDENCE_FILE_FIELDS = [
  'cancellation_policy',
  'customer_communication',
  'customer_signature',
  'duplicate_charge_documentation',
  'receipt',
  'refund_policy',
  'service_documentation',
  'shipping_documentation',
  'uncategorized_file',
] as const;

export type DisputeEvidenceTextField =
  (typeof DISPUTE_EVIDENCE_TEXT_FIELDS)[number];

export type DisputeEvidenceFileField =
  (typeof DISPUTE_EVIDENCE_FILE_FIELDS)[number];

export type DisputeActionKind = 'accept' | 'refund' | 'submit_evidence';

export interface IDisputeAction {
  action: DisputeActionKind;
  acted_at: string;
  user_name: string | null;
}

export interface IDisputeLatestAction {
  action: DisputeActionKind;
  actedAt: Date;
  userName: string | null;
}

export default class DisputeModel extends Model {
  @attr('string') provider!: string;
  @attr('string') externalId!: string;
  @attr('string') stage!: DisputeStage;
  @attr('string') status!: DisputeStatus;
  @attr('string') targetType!: DisputeTargetType;
  @attr('string') currency!: string;
  @attr('number') amount!: number;

  @attr('date') openedAt!: Date;
  @attr('date') respondBy?: Date;
  @attr('date') fundsWithdrawnAt?: Date;
  @attr('date') fundsReinstatedAt?: Date;
  @attr('date') closedAt?: Date;
  @attr('date') lastEventAt?: Date;
  @attr('date') createdAt!: Date;
  @attr('date') updatedAt!: Date;

  @attr('object-or-null') detail!: IDisputeDetail | null;

  @attr('boolean') refundable!: boolean;

  @attr('array') actions!: IDisputeAction[];

  @belongsTo('booking', { inverse: null, async: false })
  booking!: ModelRegistry['booking'];

  @cached
  get reason(): string | undefined {
    return this.detail?.reason;
  }

  @cached
  get paymentMethod(): IDisputePaymentMethod {
    return this.detail?.payment_method ?? {};
  }

  @cached
  get balanceTransactions(): IDisputeBalanceTransaction[] {
    return this.detail?.balance_transactions ?? [];
  }

  @cached
  get isUnresolved(): boolean {
    return (
      this.status === DisputeStatus.needsResponse ||
      this.status === DisputeStatus.underReview
    );
  }

  @cached
  get isInquiry(): boolean {
    return this.stage === DisputeStage.inquiry;
  }

  @cached
  get isChargeback(): boolean {
    return this.stage === DisputeStage.chargeback;
  }

  @cached
  get evidence(): IDisputeEvidence | undefined {
    return this.detail?.evidence;
  }

  @cached
  get stagedFields(): Record<string, string> {
    return this.evidence?.fields ?? {};
  }

  @cached
  get stagedFiles(): Record<string, string> {
    return this.evidence?.files ?? {};
  }

  @cached
  get hasStagedEvidence(): boolean {
    return (
      Object.keys(this.stagedFields).length !== 0 ||
      Object.keys(this.stagedFiles).length !== 0
    );
  }

  @cached
  get isRespondable(): boolean {
    const { evidence } = this;

    if (!evidence) {
      return false;
    }

    return (
      this.status === DisputeStatus.needsResponse &&
      !evidence.past_due &&
      !evidence.submitted
    );
  }

  @cached
  get latestAction(): IDisputeLatestAction | null {
    const latest = this.actions.reduce<IDisputeAction | null>(
      (winner, candidate) =>
        !winner || candidate.acted_at > winner.acted_at ? candidate : winner,
      null,
    );

    if (!latest) {
      return null;
    }

    return {
      action: latest.action,
      actedAt: new Date(latest.acted_at),
      userName: latest.user_name,
    };
  }
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    dispute: DisputeModel;
  }
}
